<template>
    <div class="w-full flex-wrap hover:border-primary-1"
        :class="pageType === 'group' && 'bg-background-3 p-8'">
        <div
                v-if="!displayImage"
                class="flex xxs:flex-col lg:flex-row justify-between rounded relative overflow-hidden xxs:px-0 lg:px-6"
                :class="[dragoverClass, pageType === 'groups' && 'bg-background-3  pt-5 pb-6']"
                @dragenter="onDragEnter"
                @drop="onDragLeave"
                @drop.prevent="handleFiles($event,'drop')"
                @dragover.prevent
                :draggable="false"
        >
            <div class="flex flex-col text-body-6 font-normal text-default-1 justify-between xxs:mr-1">
                <slot/>
                <p class="text-body-7 text-default-2 xxs:mb-2" v-if="showRecommendedSize">{{ dimensions[type].text }}</p>
                <div class="flex flex-row">
                    <label :for="`file-upload-${type}`"
                           class="connect-btn inline-flex items-center rounded focus:outline-none font-normal connect-btn-primary text-white bg-primary-1 text-body-7 text-medium py-1 px-2">
                        Upload image
                        <input :id="`file-upload-${type}`" :name="`file-upload-${type}`" type="file" :multiple="false" ref="file"
                               @change="handleFiles($event, 'upload')" class=" hidden" accept="image/png, image/jpeg"/>
                    </label>
                </div>
            </div>
            <camera :color="getAuxiliaryColor" class="xxs:mt-4 lg:mt-0"></camera>
        </div>
        <div
            v-else
            class="flex xxs:flex-col justify-between px-6 pt-5 pb-6 rounded relative overflow-hidden cursor-pointer xxs:mr-1 min-h-[140px]"
            :class="[pageType === 'groups' && 'bg-background-3', size === 'large' ? 'lg:flex-col' : 'lg:flex-row']"
        >
            <div class="flex flex-col justify-between items-start" :class="size === 'large' ? 'order-2' : 'order-1'">
                <slot/>
                <Button buttonType="primary" size="small" label="Delete image"  @click="deleteImage"/>
            </div>
            <img :class="imageClasses[type]" :src="displayImage" alt="Cropped image">
        </div>
    </div>
    <Modal v-model:show="showCropperModal" @update:show="closeModal" @close="closeModal">
        <h1 class="text-body-2 mt-3 mb-8">Crop your image</h1>
        <div class="w-full relative">
            <img id="image-container" class="w-100" :src="uploadedImage" alt="Uploaded image">
        </div>
        <div class="flex flex-row mt-6">
            <CoreButton
                    label="Choose image"
                    buttonType="primary"
                    size="default"
                    class="mr-2"
                    @click="doCrop"
            />
            <CoreButton
                label="Reset"
                buttonType="ghost"
                size="default"
                class="mr-2"
                @click="reset"
            />
        </div>
    </Modal>
</template>

<script setup>
import {ref, nextTick, watch, onUnmounted, defineEmits, onMounted, computed} from "vue";
import 'cropperjs/dist/cropper.css';
import Cropper from 'cropperjs';
import CoreButton from '@/components/core/Button.vue';
import Modal from '@/components/core/Modal.vue';
import {useTheme} from "@/stores/ThemeStore";
import Camera from "@/components/icons/Camera.vue";
import {toast} from "vue3-toastify";
import Button from "@/components/core/Button.vue";

const emit = defineEmits(['imageUpdated']);
const props = defineProps({
    type: {
        validator: function (value) {
            return ['profile', 'cover', 'thumbnail'].indexOf(value) !== -1;
        },
        default: 'profile'
    },
    pageType: {
        validator: function (value) {
            return ['group', 'profile'].indexOf(value) !== -1;
        }
    },
    imageSource: {
        type: [String, File],
        default: null,
    },
    hasButton: {
        type: Boolean,
        default: true,
    },
    aspectRatio: {
        type: Number,
        default: null,
    },
    showRecommendedSize: {
        type: Boolean,
        default: true,
    },
    size: {
        type: String,
        default: 'small',
    }
});

const TEN_MB = 1e+7;

const {getAuxiliaryColor} = useTheme();
const file = ref(null);
const dragoverClass = ref(null);
const uploadedImage = ref(null);
const showCropperModal = ref(false);
let cropperInstance = null;
const croppedImage = ref(null);

const dimensions = {
    profile: {
        aspectRatio: 1,
        text: 'Recommended size: 200x200px or any square picture',
    },
    cover: {
        aspectRatio: 4.76,
        text: 'Recommended size: 1000x210px or any 4.76 ratio picture',
    },
    thumbnail: {
        aspectRatio: 1.87,
        text: 'Recommended size: 300x160px or any 1.87 ratio picture',
    }
}

const imageClasses = ref({
    profile: {
        'w-full h-auto order-1 mb-4' : props.size === 'large',
        'h-28 w-28 rounded-[50%] order-2' : props.size !== 'large'
    },
    cover: 'xxs:h-auto xxs:w-full lg:h-10 lg:w-auto order-2 xxs:mt-6 lg:mt-4',
    thumbnail: 'xxs:h-auto xxs:w-full lg:h-20 lg:w-auto order-2 xxs:mt-6 lg:mt-4'
})


function onDragEnter() {
    dragoverClass.value = 'border border-dashed border-primary-1';
}

function onDragLeave() {
    dragoverClass.value = '';
}

function handleFiles(event, type) {
    const file = type === 'upload' ? event.target.files : event.dataTransfer.files;

    let reader = new FileReader();

    if (file[0]['size'] > TEN_MB) {
        toast.error('File size can not be bigger than 10 MB');
        return;
    }

    reader.onloadend = () => {
        uploadedImage.value = reader.result;
    }

    reader.readAsDataURL(file[0]);

    displayCropper();
}

function displayCropper() {
    showCropperModal.value = true;
}

watch(uploadedImage, (newValue) => {
    if (newValue === null) {
        return;
    }

    nextTick(() => {
        const imageContainer = document.getElementById('image-container');

        cropperInstance = new Cropper(imageContainer, {
            viewMode: 0,
            autoCropArea: 0.5,
            aspectRatio: dimensions[props.type].aspectRatio,
            guides: false,
            minContainerWidth: '100%',
        });
    })
})

function doCrop() {
    if (cropperInstance === null) {
        return;
    }

    croppedImage.value = cropperInstance.getCroppedCanvas().toDataURL();

    const croppedCanvas = cropperInstance.getCroppedCanvas();

    croppedCanvas.toBlob((blob) => {
        const file = new File([blob], 'avatar.jpg', { type: 'image/jpeg' });
        showCropperModal.value = false;
        emit('imageUpdated', file);
        showCropperModal.value = false;
    }, 'image/jpeg', 0.8);
}

function reset() {
    cropperInstance.reset();
}

function closeModal() {
    uploadedImage.value = null;
    showCropperModal.value = false;
    file.value.value = '';
}

function deleteImage() {
    uploadedImage.value = null;
    croppedImage.value = null;
    emit('imageUpdated', croppedImage.value)
}

const displayImage = computed(() => {
    return croppedImage.value || props.imageSource;
})

const toBase64 = file => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
});

onMounted(async () => {
    if(typeof props.imageSource === 'object' && props.imageSource instanceof File) {
        croppedImage.value = await toBase64(props.imageSource);
    } else {
        croppedImage.value = props.imageSource;
    }
})

onUnmounted(() => {
    if (cropperInstance) {
        cropperInstance.destroy();
    }
});

</script>